import axios from "axios";
import { chunk } from "lodash";
import { warmImageCache } from "./imageCache";

const MAX_IDS_PER_REQUEST = 100;
const BATCH_WINDOW_MS = 20;
const MEDIA_FIELDS = "id,source_url";

const resolvedUrls = new Map<number, string>();

type PendingRequest = { promise: Promise<string>; resolve: (url: string) => void };

let queued = new Map<number, PendingRequest>();
const inFlight = new Map<number, Promise<string>>();
let scheduledFlush: ReturnType<typeof setTimeout> | null = null;

function mediaEndpoint() {
  const baseUrl = String(import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");
  return `${baseUrl}/wp-json/wp/v2/media`;
}

function isUsableId(imageId: unknown): imageId is number {
  return typeof imageId === "number" && Number.isInteger(imageId) && imageId > 0;
}

export function primeMediaUrls(media: Record<string, string> | undefined | null) {
  if (media == null || typeof media !== "object") return;
  const added: string[] = [];
  Object.entries(media).forEach(([imageId, url]) => {
    const id = Number(imageId);
    if (!isUsableId(id) || typeof url !== "string" || url === "") return;
    if (resolvedUrls.get(id) !== url) added.push(url);
    resolvedUrls.set(id, url);
  });
  warmImageCache(added);
}

export function primeMediaUrlsFromEntities(entities: ({ kabelkrant_media?: Record<string, string> } | null | undefined)[] | null | undefined) {
  entities?.forEach((entity) => primeMediaUrls(entity?.kabelkrant_media));
}

export function getMediaUrl(imageId: number): Promise<string> {
  if (!isUsableId(imageId)) return Promise.resolve("");

  const resolved = resolvedUrls.get(imageId);
  if (resolved !== undefined) return Promise.resolve(resolved);

  const alreadyQueued = queued.get(imageId);
  if (alreadyQueued) return alreadyQueued.promise;

  const alreadyInFlight = inFlight.get(imageId);
  if (alreadyInFlight) return alreadyInFlight;

  let resolve!: (url: string) => void;
  const promise = new Promise<string>((resolvePromise) => {
    resolve = resolvePromise;
  });
  queued.set(imageId, { promise, resolve });

  if (scheduledFlush === null) scheduledFlush = setTimeout(flushQueue, BATCH_WINDOW_MS);

  return promise;
}

export async function getMediaUrls(imageIds: number[]): Promise<Record<number, string>> {
  const urls = await Promise.all(imageIds.map(async (imageId) => [imageId, await getMediaUrl(imageId)] as const));
  return Object.fromEntries(urls.filter(([, url]) => url !== ""));
}

async function flushQueue() {
  scheduledFlush = null;
  const batch = queued;
  queued = new Map();
  if (batch.size === 0) return;

  batch.forEach((request, imageId) => inFlight.set(imageId, request.promise));

  try {
    await Promise.all(chunk([...batch.keys()], MAX_IDS_PER_REQUEST).map(fetchMediaChunk));
  } finally {
    batch.forEach((request, imageId) => {
      inFlight.delete(imageId);
      request.resolve(resolvedUrls.get(imageId) ?? "");
    });
  }
}

async function fetchMediaChunk(imageIds: number[]) {
  try {
    const response = await axios.get<{ id: number; source_url: string }[]>(mediaEndpoint(), {
      params: {
        include: imageIds.join(","),
        per_page: imageIds.length,
        _fields: MEDIA_FIELDS
      }
    });
    const added: string[] = [];
    response.data?.forEach((media) => {
      if (!isUsableId(media?.id) || typeof media?.source_url !== "string" || media.source_url === "") return;
      if (resolvedUrls.get(media.id) !== media.source_url) added.push(media.source_url);
      resolvedUrls.set(media.id, media.source_url);
    });
    warmImageCache(added);
  } catch (error) {
    console.error("Could not resolve the media urls of the attachments", imageIds, error);
  }
}
